import { cloneDeep, isEqual } from 'lodash'
import { createStore } from './store'
import { initTableStore } from './store/config'
import { createTableContainer } from './table-container'
import { createLeftTable } from './layout/left-table'
import { createRightTable } from './layout/right-table'
import { createMainTable } from './layout/main-table'

import { createEmpty } from './components/empty/index'
import { createRow } from './components/body/row'
import { createDynamicRow } from './components/body/dynamic-row'
import { createCell } from './components/body/cell'
import { createHelperLine } from './components/helper-line'
import './style/index.scss'

import { tableProps } from './props'
import { enforceUnit, isNumber, getValue } from './utils'
import { TABLE_EVENTS, UPDATE_STORE_PARAMS, SCROLLBAR_POSITION, TABLE_CLASS_NAMES, TABLE_MODE } from './config'
import { tableCellsCopyPlugin } from '../../plugins/copy-cells/set-cell-style/element'
import { table2Plugins } from './plugins'

const { register: tablePluginRegister } = table2Plugins()

// components
const TableContainer = createTableContainer()
const LeftTable = createLeftTable()
const RightTable = createRightTable()
const MainTable = createMainTable()
const Empty = createEmpty()
const Row = createRow()
const DynamicRow = createDynamicRow()
const Cell = createCell()
const HelperLine = createHelperLine()

export default {
  name: 'KyeTable2',
  props: tableProps,
  data() {
    // 优先初始化减少更新次数
    this._initStore()
    console.log(`%cStore ------`, 'color: red;  font-weight: bold;', this.store)
    return {
      tWidth: 0,
      tHeight: 0,
      leftTable: null,
      rightTable: null,
      mainTable: null,
      highlightCellsArea: {}
    }
  },
  computed: {
    rootStyle() {
      const { style = {}, height, width, scrollbarPosition } = this.$props
      const { hScrollbarShow } = this.store.states
      const paddingBottomStyle = (hScrollbarShow && scrollbarPosition === SCROLLBAR_POSITION[0]) ? {
        paddingBottom: '12px'
      } : {}
      return enforceUnit({ ...style, ...paddingBottomStyle, height, width })
    },
    usingDynamicHeight() {
      const { enableDynamicHeight, enableExpandRow } = this.$props
      return enableDynamicHeight || enableExpandRow
    }
  },
  activated() {
    // 解决在切换模块重新回到表格内部滚动条距离变成0问题
    this.scrollTo(this.store.states.scrollPos)
  },
  mounted () {
    this._copyCellsPluginInstance = tableCellsCopyPlugin(this)
    tablePluginRegister(this)
  },
  watch: {
    columns: {
      deep: true,
      handler() {
        this._updateStoreColumns()
        // 合并行需要重新计算。当列拖动之后
        if (this.store.usingRowColMerge) {
          this.store.updateTableData()
        }
        // 存在watch比内部store computed快的情况
        this.$nextTick(() => {
          this._adjustScrollbar()
          this._updateTableHeight()
        })
      }
    },
    showIndex() {
      this._updateStoreColumns()
    },
    showCheckbox() {
      this._updateStoreColumns()
    },
    data() {
      this.store.updateTableData()
      if (this.usingDynamicHeight) {
        this.store.updateVirtualRange()
        this.store.updateVirtualConfig()
      }
      this.$nextTick(() => {
        this._updateTableHeight()
        this._updateColumnsIndexWidth()
      })
      // 清除已选的行的状态
      this._setSelectionRows({
        rowIndex: null,
        rowRenderIndex: null,
        rowData: null
      })
    },
    expandedRowKeys(val) {
      const { expandedRowKeys } = (this.store && this.store.states) || {}
      if (Array.isArray(val) && !isEqual(val, expandedRowKeys)) {
        this.store.commit(UPDATE_STORE_PARAMS, {
          expandedRowKeys: val
        })
        this.store.updateTableData()
      }
    },
    fixedRowsTop() {
      this.store.updateTableData()
    },
    fixedRowsData() {
      this.store.updateTableData()
    },
    border() {
      this._updateStoreColumns()
    },
    showSelection() {
      this._updateStoreColumns()
    },
    width() {
      const { width, height } = this.$props
      const { height: origHieght } = this.store.states
      this._changeSize({
        width,
        height: height || origHieght
      })
    },
    height() {
      const { width, height } = this.$props
      const { width: origWidth } = this.store.states
      this._changeSize({
        width: width || origWidth,
        height
      })
    },
    mergeCells () {
      this.store.transformMergeCellsToRowColSpanResult(this.$props.mergeCells)
    },
    rowHeight (val) {
      this.changeRowHeight(val)
    },
    useHorizontalVirtualScroll (val) {
      // 如果关闭的时候，需要重新更新渲染的column，因为内部有标识placeholderSign 用于渲染空节点。此时需要关闭
      if (!val) {
        this.store.updateColumns()
      }
    }
  },
  destroyed() {
    this.disposeMemory()
  },
  methods: {
    changeRowHeight (val) {
      this.store.states.rowHeight = val
      this.store.updateVirtualRange()
      // 执行一次滚动，为了正常渲染数据
      this._doScroll(this.store.states.scrollPos)
    },
    changeCollapseKeys (key) {
      this.store.changeCollapseKeysByQueue(key)
    },
    scrollTo(params = {}) {
      let { scrollTop = 0, scrollLeft = 0 } = params
      if (!isNumber(scrollTop)) return false
      if (!isNumber(scrollLeft)) return false
      this.store.states.scrollPos = { scrollTop, scrollLeft }
      this._doScroll(params)
    },
    scrollToTop(scrollTop = 0) {
      if (!isNumber(scrollTop)) return false
      this.store.states.scrollPos.scrollTop = scrollTop
      this._doScroll(this.store.states.scrollPos)
    },
    scrollToLeft(scrollLeft = 0) {
      if (!isNumber(scrollLeft)) return false
      this.store.states.scrollPos.scrollLeft = scrollLeft
      this.$refs.mainTable && this.$refs.mainTable.$refs.mainTable.scrollTo(this.store.states.scrollPos)
    },
    scrollToRow(row, strategy = 'auto') {
      const leftTableRef = this.$refs.leftTable && this.$refs.leftTable.$refs.leftTable
      leftTableRef && leftTableRef.scrollToRow(row, strategy)
      this.$refs.mainTable && this.$refs.mainTable.$refs.mainTable.scrollToRow(row, strategy)
    },
    forceUpdate() {
      const mainTableVm = this.$refs.mainTable.$refs.mainTable
      const leftTable = this.$refs.leftTable.$refs.leftTable
      const rightTable = this.$refs.rightTable.$refs.rightTable
      mainTableVm && mainTableVm.forceUpdate()
      leftTable && leftTable.forceUpdate()
      rightTable && rightTable.forceUpdate()
    },
    // select
    getSelectionRows() {
      return this.store.getSelectionRows()
    },
    toggleAllSelection() {
      this.store.toggleAllSelection()
    },
    toggleRowSelection(...arg) {
      this.store.rowSelectedChanged(...arg)
    },
    setRadioSelectedInRows(...arg) {
      this.store.setRadioSelectedInRows(...arg)
    },
    getRadioSelectionRows() {
      return this.store.getRadioSelectionRows()
    },
    setSelectedInRows(...arg) {
      this.store.setSelectedInRows(...arg)
    },
    clearSelection() {
      this.store.clearSelection()
    },
    // sort
    clearSort() {
      this.store.clearSort()
    },
    // filter
    clearFilter() {
      this.store.clearFilter()
    },
    focus() {
      const root = document.getElementById('kye-table-v2__root')
      root && root.focus()
    },
    setCurrentRow(rowData) {
      const { data, $props } = this
      let _rowData = {}
      if (typeof rowData === 'number') {
        _rowData = data[rowData]
      } else {
        _rowData = rowData
      }
      if (!_rowData) return
      const _value = getValue($props.rowKey, _rowData)
      const index = data.findIndex(item => getValue($props.rowKey, item) === _value)
      if (index || index === 0) {
        this.store.commit(UPDATE_STORE_PARAMS, {
          selectdInfo: {
            currentRow: _rowData,
            currentRowIndex: index,
          }
        })
      }
    },
    clearCurrentRow() {
      this._clearSelectionRows()
    },
    // internal
    _scroll(params) {
      this.scrollTo(params)
    },
    _doScroll(params) {
      // if (!this.$props.data.length) return
      if (this.$refs.mainTable && this.$refs.mainTable.$refs.mainTable) {
        this.$refs.mainTable.$refs.mainTable.scrollTo(params)
      }
      if (this.$refs.leftTable && this.$refs.leftTable.$refs.leftTable) {
        this.$refs.leftTable.$refs.leftTable.scrollToTop(params)
      }
      if (this.$refs.rightTable && this.$refs.rightTable.$refs.rightTable) {
        this.$refs.rightTable.$refs.rightTable.scrollToTop(params)
      }
      this.store.visibleHeplerLineNodeAnchor(false)
      this.$emit(TABLE_EVENTS.scroll, params)
      const { direction, scrollLeft } = params
      if (direction === 'X') {
        this.$el && this.$el.classList[scrollLeft <= 0 ? 'remove' : 'add']('kye-table-v2_box-shadow')
      }
    },
    _rowExpanded(expanded) {
      this.$emit(TABLE_EVENTS.rowExpand, expanded)
      this.store.onRowExpanded(expanded)
    },
    _initStore() {
      this.store = createStore(this)
      initTableStore({ table: this })
    },
    _changeSize({ width, height } = {}) {
      const { maxHeight: $maxHeight, mode } = this.$props
      // 这里的width永远是最大maxWdith
      const _maxWidth = width
      const _maxHeight = height
      // 取最大限制
      if ($maxHeight && height > $maxHeight && mode !== TABLE_MODE.INHERIT) {
        height = $maxHeight
      }
      this.store.commit(UPDATE_STORE_PARAMS, {
        height,
        width,
        maxWidth: _maxWidth,
        maxHeight: $maxHeight ? Math.min($maxHeight, _maxHeight) : _maxHeight
      })
      this.store.updateColumns()
      this._updateVirtualConfig()
      this.$emit(TABLE_EVENTS.changeSize, { width, height })
    },
    _updateStoreColumns() {
      this.store.states.columns = cloneDeep(this.columns)
      this.store.flatColumns()
      this.store.updateColumns()
    },
    _updateVirtualConfig() {
      const { scrollPos } = this.store.states
      this.store.updateVirtualConfig()
      this.store.resetScroll(scrollPos)
    },
    _table2Keydown(e) {
      const { selectdInfo, tableDataLength, renderData, cacheSize, rowHeight } = this.store.states
      const { currentRow, currentRowIndex, renderDataIndex } = selectdInfo
      const renderDataLength = renderData.length - 1
      const scrollIndex = renderDataLength - cacheSize
      const { keyCode } = e
      const _dataLength = tableDataLength - 1
      let _top
      // 上下按键阻止默认行为
      // if ([40, 38].includes(keyCode)) {
      //   e.preventDefault()
      // }
      switch (keyCode) {
        case 40: // 方向键↓
          if (currentRowIndex === _dataLength) return
          let nextRowIndex = currentRowIndex + 1
          let nextRenderIndex = renderDataIndex
          // 滚动联动
          if (nextRenderIndex >= scrollIndex) {
            // 到底继续往下走 rowHeight为容错
            const isbottom = _dataLength - currentRowIndex <= cacheSize
            if (isbottom) {
              nextRenderIndex += 1
            }
            const num = (nextRowIndex - scrollIndex) + 0.5 // 0.5为容错
            _top = Math.max(0, Math.floor(num * rowHeight))
            this.scrollToTop(_top)
          } else {
            nextRenderIndex += 1
          }
          this._setSelectionRows({
            rowIndex: nextRowIndex,
            rowRenderIndex: nextRenderIndex,
            rowData: renderData[nextRenderIndex]
          })
          break
        case 38: // 方向键↑
          if (currentRowIndex === 0) return
          const preRowIndex = currentRowIndex - 1
          let preRenderIndex = renderDataIndex
          // 滚动联动
          if (preRenderIndex <= cacheSize) {
            const isTop = _dataLength - currentRowIndex >= cacheSize
            if (isTop) {
              preRenderIndex -= 1
            }
            const num = (preRowIndex - cacheSize) + 0.5 // 0.5为容错
            _top = Math.max(0, Math.floor(num * rowHeight))
            this.scrollToTop(_top)
          } else {
            preRenderIndex -= 1
          }
          if (preRenderIndex < 0) {
            preRenderIndex = 0
          }
          this._setSelectionRows({
            rowIndex: preRowIndex,
            rowRenderIndex: preRenderIndex,
            rowData: renderData[preRenderIndex]
          })
          break
        case 13: // 回车键
          if (currentRow) {
            this.$emit(TABLE_EVENTS.rowDblclick, {
              rowData: currentRow,
              rowIndex: currentRowIndex,
              renderDataIndex,
              eventType: 'enter'
            })
          }
          break
      }
    },
    _clearSelectionRows() {
      this._setSelectionRows({
        rowIndex: null,
        rowRenderIndex: null,
        rowData: null
      })
    },
    _setSelectionRows({ rowIndex, rowRenderIndex, rowData } = {}) {
      this.store.commit(UPDATE_STORE_PARAMS, {
        selectdInfo: {
          currentRow: rowData,
          currentRowIndex: rowIndex,
          renderDataIndex: rowRenderIndex
        }
      })
    },
    _updateTableHeight() {
      const { tableContainer } = this.$refs
      if (tableContainer) {
        tableContainer.handleResize()
      }
    },
    _updateColumnsIndexWidth() {
      const { mainColumns } = this.store.states
      const indexCol = String(this.store._curMaxIndexVal).length * 10
      const indexColumn = mainColumns.find(column => column.key === '_index')
      if (indexColumn && indexColumn.width !== indexCol) {
        this.store.updateColumns()
      }
    },
    _adjustScrollbar() {
      if (this.store && this.store.adjustScrollbar) {
        this.store.adjustScrollbar()
      }
    },
    disposeMemory() {
      if (this.store) {
        // store
        this.store.$destroy()
        this.store.table = null
        this.store.tableData = null
        this.store._checkboxData = null
        this.store.horizontalVirtual = null
        this.store.virtualInstance = null
        this.store._checkboxData = null
        this.store.states = {}
        this.store.selection = {}
        this.store._watchers = []
        this.store._computedWatchers = []
        this.store._routerRoot = null
        this.store._renderProxy = null
        // table
        this.popoverPluginInstance = null
        this._copyCellsPluginInstance = null
        this._copyGridCellsInstance = null
        this._copySelection = null
        this._events = []
        this._watchers = []
        this.$children = []
        this._vnode = null
        this.store = null
        this.highlightCellsArea = null
        this._events = {}
        this._watchers = []
      }
    }
  },
  render() {
    const store = this.store
    const props = this.$props
    const isEmptyData = props.data.length === 0
    // props
    const commonAttribute = {
      props: {
        store: this.store,
        rowHeight: props.rowHeight,
        showIndex: props.showIndex,
        headerAlign: props.headerAlign,
        emptyText: props.emptyText,
        fixedRowsTop: props.fixedRowsTop,
        fixedRowsData: props.fixedRowsData,
        footerRows: props.footerRows,
        border: props.border,
        scrollbarAlwaysOn: props.scrollbarAlwaysOn,
        highlightCurrentRow: props.highlightCurrentRow,
        showIndexType: props.showIndexType,
        enableDynamicHeight: this.usingDynamicHeight,
        useHorizontalVirtualScroll: props.useHorizontalVirtualScroll,
        // methods
        scroll: this._scroll
      },
    }
    const mainProps = {
      props: {
        ...commonAttribute.props,
        columns: store.states.mainColumns,
        width: store.tableWidth,
        height: store.tableBodyHeight,
        headerWidth: store.states.width,
        headerHeight: props.headerHeight,
        rowTotalHeight: store.rowTotalHeight,
        hScrollbarSize: props.hScrollbarSize,
        vScrollbarSize: props.vScrollbarSize,
        scrollbarStyle: props.scrollbarStyle,

        bodyWidth: store.mainColumnsTotalWidth,
        bodyHeight: store.rowTotalHeight,
        tableWidth: store.states.width
      },
      ref: 'mainTable',
      class: ['kye-table-v2__main-table']
    }
    const leftProps = {
      props: {
        ...commonAttribute.props,
        columns: store.states.fixedColumnsOnLeft,
        width: store.leftTableWidth,
        height: store.tableBodyHeight,
        headerWidth: store.leftTableWidth,
        headerHeight: props.headerHeight,
        rowTotalHeight: store.rowTotalHeight,

        bodyWidth: store.mainColumnsTotalWidth,
        bodyHeight: store.rowTotalHeight,
        tableWidth: store.tableWidth,
        showEmpty: isEmptyData
      },
      ref: 'leftTable',
      class: ['kye-table-v2__left-table'],
    }
    const rightProps = {
      props: {
        ...commonAttribute.props,
        columns: store.states.fixedColumnsOnRight,
        width: store.rightTableWidth,
        height: store.tableBodyHeight,
        headerWidth: store.rightTableWidth,
        headerHeight: props.headerHeight,
        rowTotalHeight: store.rowTotalHeight,

        bodyWidth: store.mainColumnsTotalWidth,
        bodyHeight: store.rowTotalHeight,
        tableWidth: store.tableWidth,
        showEmpty: isEmptyData
      },
      ref: 'rightTable',
      class: ['kye-table-v2__right-table']
    }
    // slot
    const slot = this.$slots.default ? this.$slots.default[0] : this.$scopedSlots
    const tableRowProps = {
      rowKey: props.rowKey,
      depthMap: store.depthMap,
      indentSize: props.indentSize,
      expandColumnKey: props.expandColumnKey,
      iconSize: props.iconSize,
      expandedRowKeys: store.states.expandedRowKeys,
      rowHeight: props.rowHeight,
      treeProps: props.treeProps,
      loadingRow: store.states.loadingRow,
      dynamicSizeMap: store.states.dynamicSizeMap,
      enableExpandRow: props.enableExpandRow,
      tableBodyHeight: store.tableBodyHeight,
      dynamicRowOffsetYMap: store.dynamicRowOffsetYMap,
      mainColumns: store.states.mainColumns,
      dynamicRowSize: props.dynamicRowSize,
      // methods
      onRowExpanded: this._rowExpanded,
    }
    const tableCellProps = {
      isAllSelected: store.states.isAllSelected,
      UPDATE_checkbox: store.states.UPDATE_checkbox,
      rowSelectedChanged: store.rowSelectedChanged,
      getSelection: store.getSelection,
      setRadioSelectedInRows: store.setRadioSelectedInRows,
      getRadioSelected: store.getRadioSelected,
      radioProps: props.radioProps,
      selectable: props.selectable,
      rowKey: props.rowKey,
      cellStyle: props.cellStyle,
      cellClassName: props.cellClassName,
      showOverflowTooltip: props.showOverflowTooltip,
      lastIndexMergeMaps: store.states.lastIndexMergeMaps,
      tableDataLength: store.states.tableDataLength,
      enableDynamicHeight: this.usingDynamicHeight,
      tableBodyHeight: store.tableBodyHeight,
      noWrap: props.noWrap,
      highlightCellsArea: this.highlightCellsArea,
      rowCellCanSelected: props.rowCellCanSelected,
      mergeCellsScope: props.mergeCellsScope,
      // methods
      spanMethod: props.spanMethod,
      getColRowSpanResult: store.getColRowSpanResult,
      getFooterRowsColRowSpanResult: store.getFooterRowsColRowSpanResult,
      getFixedRowsColRowSpanResult: store.getFixedRowsColRowSpanResult
    }
    const RowComponent = this.usingDynamicHeight ? DynamicRow : Row
    const tableSlots = {
      row: (rowProps) => {
        return (
          <RowComponent {
            ...{
              props: {
                showSelection: props.showSelection,
                ...rowProps,
                ...tableRowProps,
                originRowData: props.data[rowProps.rowIndex]
              }
            }}>
            {{
              row: slot.row || this.$scopedSlots.row,
              cell: (cellProps) => <Cell {
                ...{
                  props: {
                    ...tableCellProps,
                    ...cellProps,
                    fixedRowsTop: rowProps.fixedRow ? 0 : props.fixedRowsTop,
                    isFooter: rowProps.isFooter,
                    isFixedRow: rowProps.isFixedRow,
                  }
                }}>
                {{
                  cell: slot.cell || this.$scopedSlots.cell
                }}
              </Cell>,
              expandRow: slot.expandRow || this.$scopedSlots.expandRow
            }}
          </RowComponent>
        )
      },
      header: slot.header,
      headerTh: slot.headerTh,
      headerCell: slot.headerCell || this.$scopedSlots.headerCell,
      empty: slot.empty || this.$slots.empty,
      footer: slot.footer || this.$slots.footer
    }

    const rootClass = [
      TABLE_CLASS_NAMES.root,
      props.scrollbarAlwaysOn === false ? `${TABLE_CLASS_NAMES.root}_scrollbarHover` : ''
    ]

    const isEmpty = isEmptyData && props.columns.length === 0
    if (isEmpty && props.showEmpty) return slot.empty || <Empty style={this.rootStyle} emptyText={props.emptyText}></Empty>
    const mouseleave = (e) => {
      this.$emit('table-leave', e)
    }
    return (
      <div
        tabindex="0"
        id={TABLE_CLASS_NAMES.root}
        class={rootClass.filter(Boolean)}
        style={this.rootStyle}
        on-keydown={this._table2Keydown}
        on-mouseleave={mouseleave}
        v-loading={props.loading}
        element-loading-text={props.loadingText}
        element-loading-spinner={props.loadingSpinner}
        element-loading-background={props.loadingBackground}
        element-loading-custom-class={props.loadingCustomClass}>
        <TableContainer
          ref="tableContainer"
          store={this.store}
          height={props.height}
          width={props.width}
          border={props.border}
          maxHeight={props.maxHeight}
          onChangeSize={this._changeSize}>
          <MainTable {...mainProps} >
            { tableSlots }
          </MainTable>
          <LeftTable {...leftProps}>
            { tableSlots }
          </LeftTable>
          <RightTable {...rightProps}>
            { tableSlots }
          </RightTable>
          <HelperLine direction='vertical' store={this.store}></HelperLine>
        </TableContainer>
      </div>
    )
  }
}




// WEBPACK FOOTER //
// ./src/public/components/kye-table2/table.js